import { useState, useMemo, useCallback, useEffect } from 'react';

import DroneIcon from '../../../assets/images/VehicleArrowOpaque.svg';
import { SharedSessionParticipant, SharedSessionResponse } from '../../../services/types/api.generated';
import { createGimbalIndicatorImage } from '../../GimbalIndicator';
import { LiveLineData, MarkerData } from '../../Mapbox/mapTypes';
import { InstrumentSettings } from '../CrewInstrumentSettingsModal';

const DRONE_MARKER_SIZE = 49;
const SMALL_DRONE_SIZE = 20;

export const useCrewViewState = (deviceUuid: string, isFullscreenVideo: boolean) => {
  const [isPending, setIsPending] = useState(false);
  const [participants, setParticipants] = useState<SharedSessionParticipant[]>([]);
  const [sessionData, setSessionData] = useState<SharedSessionResponse | null>(null);
  const [markerImages, setMarkerImages] = useState<{ [id: string]: MarkerData }>({});
  const [instrumentSettings, setInstrumentSettings] = useState<InstrumentSettings>({
    attitudeIndicator: true,
    compass: true,
    thrust: true,
    battery: true
  });
  const [isInstrumentsVisible, setIsInstrumentsVisible] = useState(true);
  const [isInstrumentSettingsModalOpen, setIsInstrumentSettingsModalOpen] = useState(false);
  const [activeVideoServerUrl, setActiveVideoServerUrl] = useState<string | null>(null);
  const [pingVideoUrl, setPingVideoUrl] = useState<string | null | undefined>(undefined);
  const [agl, setAgl] = useState<string>('0.0');

  // Create marker images dynamically based on view mode (fullscreen vs PiP)
  const droneMarkerImage = useMemo(() => {
    const size = isFullscreenVideo ? DRONE_MARKER_SIZE : SMALL_DRONE_SIZE;
    const img = new Image();
    img.src = DroneIcon;
    img.width = size;
    img.height = size;
    return img;
  }, [isFullscreenVideo]);

  const gimbalMarkerImage = useMemo(() => {
    const size = isFullscreenVideo ? DRONE_MARKER_SIZE : SMALL_DRONE_SIZE;
    return createGimbalIndicatorImage(size);
  }, [isFullscreenVideo]);

  const [liveLine, setLiveLine] = useState<LiveLineData>({
    lines: [],
    marker: droneMarkerImage,
    gimbal: gimbalMarkerImage,
    gimbalAngle: 0,
    canUpdateMarkerLocation: true,
    deviceId: deviceUuid || ''
  });

  // Update liveLine markers when marker images change (due to view mode change)
  useEffect(() => {
    setLiveLine((prev) => ({
      ...prev,
      marker: droneMarkerImage,
      gimbal: gimbalMarkerImage
    }));
  }, [droneMarkerImage, gimbalMarkerImage]);

  const updateAgl = useCallback((newAgl: string) => {
    setAgl(newAgl);
  }, []);

  const toggleInstrumentsShowState = useCallback(() => {
    setIsInstrumentsVisible(!isInstrumentsVisible);
  }, [isInstrumentsVisible]);

  const handleOpenInstrumentSettings = useCallback(() => {
    setIsInstrumentSettingsModalOpen(true);
  }, []);

  return {
    isPending,
    setIsPending,
    participants,
    setParticipants,
    sessionData,
    setSessionData,
    markerImages,
    setMarkerImages,
    instrumentSettings,
    setInstrumentSettings,
    isInstrumentsVisible,
    toggleInstrumentsShowState,
    isInstrumentSettingsModalOpen,
    setIsInstrumentSettingsModalOpen,
    activeVideoServerUrl,
    setActiveVideoServerUrl,
    pingVideoUrl,
    setPingVideoUrl,
    agl,
    updateAgl,
    liveLine,
    setLiveLine,
    handleOpenInstrumentSettings
  };
};
